import { useState } from "react";
import { formatSalesValue } from "./../../utils/formatUtils";
import { FaSpinner, FaExclamationTriangle } from "react-icons/fa";
import { CustomModal } from "./../Modal/CustomModal";
import DataTable from "./DataTable";

const AGEING_CATEGORIES = ["0-30", "31-60", "61-90", "91-180", "181-365", "366+"];

const AgeingDebtors = ({ debtData, error, loading }) => {
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
        setModalOpen(true);
    };

    const sumCategory = (field) =>
        AGEING_CATEGORIES.reduce((sum, category) => sum + (debtData?.[category]?.[field] || 0), 0);

    const modalRows = debtData?.[selectedCategory]?.data || [];

    return (
        <div className="max-w-6xl mx-auto w-full">
            {loading ? (
                <div className="flex justify-center items-center gap-2 py-8 text-gray-600">
                    <FaSpinner className="animate-spin text-lg text-blue-500" />
                    <span className="font-medium">Loading debtors ageing summary...</span>
                </div>
            ) : error ? (
                <div className="flex items-center justify-center gap-2 p-3 border border-red-200 bg-red-50 rounded-md text-red-600 font-medium">
                    <FaExclamationTriangle />
                    {error}
                </div>
            ) : !debtData || Object.keys(debtData).length === 0 ? (
                <div className="text-center font-medium p-3 border border-gray-200 rounded-md text-gray-500">
                    Data not available
                </div>
            ) : (
                <div className="overflow-x-auto tiny-scrollbar rounded-md border border-gray-100">
                    <table className="w-full text-sm text-center">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-gray-600">
                                    Debtor Type
                                </th>
                                {AGEING_CATEGORIES.map((category) => (
                                    <th
                                        key={category}
                                        className="px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-gray-600"
                                    >
                                        <button
                                            onClick={() => handleCategoryClick(category)}
                                            className="hover:text-blue-600 hover:underline transition"
                                        >
                                            {category}
                                        </button>
                                    </th>
                                ))}
                                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-gray-600">
                                    Net Due
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            <tr>
                                <td className="px-3 py-2 font-semibold text-gray-700">Individual</td>
                                {AGEING_CATEGORIES.map((category) => (
                                    <td key={category} className="px-3 py-2 text-gray-700">
                                        {formatSalesValue(debtData[category]?.totalInitial || 0)}
                                    </td>
                                ))}
                                <td className="px-3 py-2 font-medium text-gray-800">
                                    {formatSalesValue(sumCategory("totalInitial"))}
                                </td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 font-semibold text-gray-700">Payments</td>
                                {AGEING_CATEGORIES.map((category) => (
                                    <td key={category} className="px-3 py-2 text-gray-700">
                                        {formatSalesValue(debtData[category]?.totalPayment || 0)}
                                    </td>
                                ))}
                                <td className="px-3 py-2 font-medium text-gray-800">
                                    {formatSalesValue(sumCategory("totalPayment"))}
                                </td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 font-semibold text-gray-700">Remaining</td>
                                {AGEING_CATEGORIES.map((category) => (
                                    <td key={category} className="px-3 py-2 text-gray-700">
                                        {formatSalesValue(debtData[category]?.remainingTotal || 0)}
                                    </td>
                                ))}
                                <td className="px-3 py-2 font-medium text-gray-800">
                                    {formatSalesValue(sumCategory("remainingTotal"))}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            <CustomModal show={modalOpen} onHide={() => setModalOpen(false)} size="xl" scrollable>
                <CustomModal.Header closeButton onHide={() => setModalOpen(false)}>
                    <CustomModal.Title>Details for {selectedCategory} Days</CustomModal.Title>
                </CustomModal.Header>

                <CustomModal.Body>
                    <DataTable
                        columns={[
                            { key: "customerName", label: "Customer Name" },
                            { key: "guestID", label: "Guest ID" },
                            {
                                key: "InitialTotal",
                                label: "Initial Total",
                                render: (r) => formatSalesValue(r.InitialTotal),
                            },
                            {
                                key: "PaymentTotal",
                                label: "Payment Total",
                                render: (r) => formatSalesValue(r.PaymentTotal),
                            },
                        ]}
                        rows={modalRows}
                        rowKey={(r) => r.guestID}
                    />
                </CustomModal.Body>

                <CustomModal.Footer className="flex justify-end">
                    <button
                        onClick={() => setModalOpen(false)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition"
                    >
                        Close
                    </button>
                </CustomModal.Footer>
            </CustomModal>
        </div>
    );
};

export default AgeingDebtors;